/**
 * WelcomeGate — Pantalla de bienvenida (v3, Google-only)
 *
 * Flujo unificado:
 *   Ambos tipos (Particular y Comercio) inician sesión con Google.
 *   La única diferencia es el "intent" que persistimos ANTES del OAuth,
 *   para que al volver el AppGate sepa qué customer_type asignar.
 *
 *   - Botón "Soy Particular"  → localStorage.intent='consumidor_final' → Google
 *   - Botón "Soy Comercio"    → localStorage.intent='comercio'          → Google
 *
 * Al hacer click en cualquiera de los dos botones, primero mostramos un
 * mini modal instructivo con los pasos que Google va a pedir. Recién
 * cuando el usuario confirma, redirigimos al OAuth.
 *
 * Persistimos también un legado 'bebida-bacana-guest-type' con el mismo
 * valor por compatibilidad con lógicas viejas (ThemeContext, etc.).
 */
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Store, User, ArrowRight, ShoppingBag, Truck, Zap, Tag,
  Percent, Star, TrendingUp, Handshake, ShieldCheck,
  X as XIcon
} from 'lucide-react';

const KEY_TYPE = 'bebida-bacana-guest-type';       // legado
const KEY_INTENT = 'bebida-bacana-login-intent';   // 'consumidor_final' | 'comercio'

const formatPrice = (n) => `$${Math.ceil(n || 0).toLocaleString('es-AR')}`;

const WelcomeGate = ({ storeSettings, theme, onContinue: _onContinue }) => {
  const { loginWithGoogle, isFeatureEnabled } = useAuth();
  const [confirm, setConfirm] = useState(null); // null | 'consumidor_final' | 'comercio'
  // 🚦 Si el flag b2cFlow está OFF, la app es exclusiva para comercios y
  // ocultamos por completo la opción "Soy Particular".
  const b2cEnabled = isFeatureEnabled ? isFeatureEnabled('b2cFlow') : true;

  const retailMin = storeSettings?.retailMinimumPurchase || 100000;

  // Persistimos el intent ANTES de redirigir a Google. Al volver del
  // OAuth, AppGate lo lee y setea customer_type automáticamente.
  const startLogin = (intent) => {
    try {
      localStorage.setItem(KEY_INTENT, intent);
      localStorage.setItem(KEY_TYPE, intent); // compat con lógica vieja
    } catch { /* storage lleno */ }
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.background }}>
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 relative" data-testid="welcome-gate">

        {/* Header */}
        <div className="px-6 py-6 text-center border-b border-slate-100"
             style={{ background: `linear-gradient(135deg, ${theme.primary}10, ${theme.primary}05)` }}>
          <img
            src={storeSettings?.logo || '/logo.png'}
            alt={storeSettings?.storeName || 'Bebida Bacana'}
            className="h-32 mx-auto mb-3 object-contain"
            onError={(e) => { e.currentTarget.src = '/logo.png'; }}
          />
          <h1 className="text-2xl font-black" style={{ color: theme.text, fontFamily: 'Barlow Condensed, sans-serif' }}>
            {storeSettings?.storeName || 'Bebida Bacana'}
          </h1>
          <p className="text-xs text-slate-500 mt-1">Bebidas mayoristas</p>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-3">
          <p className="text-sm text-slate-700 leading-snug text-center mb-1">
            <b>¡Bienvenido!</b> Elegí cómo querés comprar. Cada opción tiene sus condiciones.
          </p>

          {/* COMERCIO (primero, es el principal) */}
          <button onClick={() => setConfirm('comercio')}
            data-testid="btn-soy-comercio"
            className="w-full text-left p-4 border-2 border-emerald-200 hover:border-emerald-500 rounded-2xl transition-all group bg-emerald-50/30">
            <div className="flex items-start gap-3 mb-2">
              <div className="p-2 rounded-xl bg-emerald-100 text-emerald-700"><Store size={22} /></div>
              <div className="flex-1">
                <div className="font-black text-slate-900 text-base">Soy Comercio</div>
                <p className="text-[11px] text-slate-500 mt-0.5">Para kioscos, distribuidores y revendedores.</p>
              </div>
              <ArrowRight className="text-slate-400 group-hover:text-emerald-600 transition-colors flex-shrink-0" size={18} />
            </div>
            <ul className="space-y-1 pl-1">
              <li className="flex items-start gap-2 text-[11px] text-slate-700">
                <Tag size={13} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <span><b>Precios mayoristas</b> en todo el catálogo.</span>
              </li>
              <li className="flex items-start gap-2 text-[11px] text-slate-700">
                <Star size={13} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>Promociones exclusivas para comercios.</span>
              </li>
              <li className="flex items-start gap-2 text-[11px] text-slate-700">
                <Handshake size={13} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>Condiciones comerciales preferenciales.</span>
              </li>
              <li className="flex items-start gap-2 text-[11px] text-slate-700">
                <Percent size={13} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>Descuentos según forma de pago.</span>
              </li>
              <li className="flex items-start gap-2 text-[11px] text-slate-700">
                <TrendingUp size={13} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>Beneficios exclusivos y seguimiento de pedidos.</span>
              </li>
            </ul>
          </button>

          {/* CONSUMIDOR FINAL (segundo, opción secundaria) — oculto si b2cFlow OFF */}
          {b2cEnabled && (
          <button onClick={() => setConfirm('consumidor_final')}
            data-testid="btn-soy-particular"
            className="w-full text-left p-4 border-2 border-slate-200 hover:border-amber-400 rounded-2xl transition-all group">
            <div className="flex items-start gap-3 mb-2">
              <div className="p-2 rounded-xl bg-amber-100 text-amber-700"><User size={22} /></div>
              <div className="flex-1">
                <div className="font-black text-slate-900 text-base">Soy Particular</div>
                <p className="text-[11px] text-slate-500 mt-0.5">Ideal para consumidores finales.</p>
              </div>
              <ArrowRight className="text-slate-400 group-hover:text-amber-600 transition-colors flex-shrink-0" size={18} />
            </div>
            <ul className="space-y-1 pl-1">
              <li className="flex items-start gap-2 text-[11px] text-slate-700">
                <Tag size={13} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <span>Precios para consumidor final.</span>
              </li>
              <li className="flex items-start gap-2 text-[11px] text-slate-700">
                <ShoppingBag size={13} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <span>Compra mínima: <b>{formatPrice(retailMin)}</b>.</span>
              </li>
              <li className="flex items-start gap-2 text-[11px] text-slate-700">
                <Truck size={13} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <span>Entrega a domicilio según zona.</span>
              </li>
              <li className="flex items-start gap-2 text-[11px] text-slate-700">
                <Zap size={13} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <span>Compra rápida y sencilla.</span>
              </li>
            </ul>
          </button>
          )}

          {!b2cEnabled && (
            <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-4 text-center" data-testid="b2c-disabled-notice">
              <p className="text-xs text-slate-600 leading-snug">
                Bebida Bacana es una plataforma <strong>exclusiva para comercios</strong>.<br />
                Registrate arriba para empezar a comprar al por mayor.
              </p>
            </div>
          )}

          <p className="text-[11px] text-slate-400 text-center pt-2">
            Ingresás una sola vez con Google y quedás dentro. Podés cambiar tu elección desde el menú de la app.
          </p>
        </div>

        {/* Modal de confirmación / instructivo Google */}
        {confirm && (
          <ConfirmGoogleLogin
            intent={confirm}
            theme={theme}
            onCancel={() => setConfirm(null)}
            onConfirm={() => { setConfirm(null); startLogin(confirm); }}
          />
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Modal instructivo: qué le va a pedir Google al usuario.
// Un solo click más pero baja mucho la ansiedad del "¿qué me está pidiendo?"
// ─────────────────────────────────────────────────────────────
const ConfirmGoogleLogin = ({ intent, theme, onCancel, onConfirm }) => {
  const isComercio = intent === 'comercio';
  const color = isComercio ? '#059669' : '#D97706';
  const label = isComercio ? 'Soy Comercio' : 'Soy Particular';
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4"
         data-testid="google-instruct-modal">
      <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden">
        <div className="relative px-5 pt-5 pb-3 border-b border-slate-100">
          <button onClick={onCancel} className="absolute top-3 right-3 p-1.5 hover:bg-slate-100 rounded-full"
            data-testid="close-google-modal">
            <XIcon size={18} className="text-slate-400" />
          </button>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color }}>{label}</span>
          </div>
          <h3 className="text-lg font-black text-slate-900 mt-1">Iniciá sesión con Google</h3>
          <p className="text-xs text-slate-500 mt-1">Es la forma más rápida y segura de entrar. No usamos contraseñas.</p>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-xs font-bold text-slate-700">Google va a pedirte:</p>
          <div className="space-y-2">
            <StepBadge n={1} title="Elegir tu cuenta de Gmail"
              detail="Si tenés más de una, seleccioná la que uses habitualmente." />
            <StepBadge n={2} title="Confirmar tu email y nombre"
              detail="Google nos comparte sólo tu email, nombre y foto. Nada más." />
            <StepBadge n={3} title="Volver a Bebida Bacana"
              detail="Te devolvemos automáticamente a la app y quedás dentro." />
          </div>

          <div className="flex items-start gap-2 mt-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
            <ShieldCheck size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-slate-600 leading-snug">
              Nunca vemos tu contraseña de Google. La sesión queda guardada en tu celular
              y no te vamos a pedir loguearte de nuevo cada vez.
            </p>
          </div>
        </div>

        <div className="px-5 pb-5 pt-1 flex flex-col gap-2">
          <button onClick={onConfirm}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-bold text-sm shadow-lg active:scale-95 transition-transform"
            style={{ backgroundColor: color }}
            data-testid="confirm-google-login-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar con Google
          </button>
          <button onClick={onCancel}
            className="w-full py-2 text-xs text-slate-500 hover:text-slate-800">
            Volver
          </button>
        </div>
      </div>
    </div>
  );
};

const StepBadge = ({ n, title, detail }) => (
  <div className="flex items-start gap-3">
    <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">
      {n}
    </div>
    <div className="flex-1">
      <p className="text-xs font-bold text-slate-900 leading-tight">{title}</p>
      <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{detail}</p>
    </div>
  </div>
);

export default WelcomeGate;
